(function () {
  // Shared hero-anchor relocation for the header-group glass shape system
  // (snippets/sindhi-glass-shape.liquid). The announcement bar, utility bar
  // and location bar can each independently have a shape active, and none of
  // them sit immediately before the hero in the DOM (unlike SP: Header, which
  // anchors to its own section slot), so becoming a real DOM child of the
  // hero is their only way to actually float over hero content.
  //
  // Round 2 (2026-08-02, Sanjay: "it's over the NAV ... should be shorter
  // than the nav"). Measured on the live preview: the relocated announcement
  // bar and the nav row both landed at exactly y=58 — a guaranteed collision,
  // not bad luck. Both derive from the same origin: the header's own section
  // slot collapses to zero height when it goes absolute, so the hero's top
  // edge and the header's top edge are the SAME y, and each then adds its own
  // 14px island margin. Anchoring every bar at the hero's top:0 stacks them
  // all onto the nav.
  //
  // Fix: relocated bars go into ONE .sdf-glass-stack wrapper (absolutely
  // positioned at the hero's top edge) and flow normally inside it, so they
  // stack vertically in DOM order instead of piling onto one coordinate. The
  // wrapper's measured height is published as --sdf-glass-stack-offset, which
  // SP: Header's own absolute `top` reads, so the nav sits below the floating
  // bars rather than underneath them.
  var STACK_CLASS = 'sdf-glass-stack';
  var resizeObserver = null;
  var navResizeObserver = null;
  var navResizeTimer = null;
  // Where each bar lived before it was moved, so mobile can put it back.
  var origin = typeof WeakMap === 'function' ? new WeakMap() : null;

  // Hero-floating is desktop-only. The mobile header is position:fixed with
  // its own mh--shape-* system, so the hero (and anything anchored to its top)
  // renders UNDERNEATH it — measured at 390px, the bars landed inside the
  // header's own band and were simply covered, which is what made a hidden
  // announcement bar look like it had hidden the delivery bar too. Pushing the
  // stack down didn't hold either, because the mobile header's own offset
  // moves with it. Normal flow can't overlap a fixed header, so below 1024px
  // the bars stay exactly where Shopify rendered them.
  var DESKTOP_MIN = 1024;

  function restoreAll() {
    var moved = document.querySelectorAll('[data-glass-shape].is-hero-anchored');
    Array.prototype.forEach.call(moved, function (el) {
      var o = origin && origin.get(el);
      el.classList.remove('is-hero-anchored');
      if (!o || !o.parent) return;
      if (o.next && o.next.parentElement === o.parent) o.parent.insertBefore(el, o.next);
      else o.parent.appendChild(el);
    });
    var stack = document.querySelector('.' + STACK_CLASS);
    if (stack) stack.remove();
    document.documentElement.style.setProperty('--sdf-glass-stack-offset', '0px');
  }

  function publishOffset(stack) {
    var h = stack && stack.children.length ? Math.round(stack.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--sdf-glass-stack-offset', h + 'px');
  }

  // A relocated bar is orphaned when its original section slot has been
  // removed from the document (theme editor "hide section"), or is still
  // present but hidden — in both cases the merchant expects the bar gone, and
  // the relocated copy is no longer inside the wrapper the theme is hiding.
  // Which shape applies at the CURRENT width. Settings are split into
  // Desktop+Tablet (>=750px) and Mobile (<=749px), so an element can float on
  // one and sit in normal flow on the other.
  function activeShape(el) {
    var attr = window.innerWidth >= 750 ? 'data-glass-shape-d' : 'data-glass-shape-m';
    var v = el.getAttribute(attr);
    return (!v || v === 'none') ? null : v;
  }

  // Put a single bar back where Shopify originally rendered it.
  function restoreOne(el) {
    var o = origin && origin.get(el);
    el.classList.remove('is-hero-anchored');
    if (!o || !o.parent || !document.contains(o.parent)) return;
    if (o.next && o.next.parentElement === o.parent) o.parent.insertBefore(el, o.next);
    else o.parent.appendChild(el);
  }

  function isOrphaned(el) {
    if (!origin) return false;
    var o = origin.get(el);
    if (!o || !o.parent) return false;
    if (!document.contains(o.parent)) return true;
    return getComputedStyle(o.parent).display === 'none';
  }

  // Pairing the announcement bar with the delivery bar is pure layout — it does
  // not need a hero, only the floating does. Because relocateGlassShapes()
  // returns early when no .sindhi-hero exists, the whole combine step used to be
  // skipped on every template except the homepage, so "Sit beside the
  // announcement bar" silently did nothing there (Sanjay, 2026-08-03: "on any
  // other page other than homepage, location bar is not appearing beside the
  // announcement bar even when setting is enabled"). Same .sdf-glass-row markup
  // as the hero path, just left in normal flow — the row's CSS never depended on
  // being inside .sdf-glass-stack.
  function combineInFlow() {
    var announcement = document.querySelector('[id^="announcement-bar-"]');
    var partner = document.querySelector('[data-glass-combine="true"]');
    var row = document.querySelector('.sdf-glass-row');

    if (!announcement || !partner || partner === announcement) {
      // Setting switched off, or one of the bars is hidden at this breakpoint —
      // unwrap a row built on an earlier pass rather than stranding the bars.
      if (row && row.parentElement) {
        while (row.firstChild) row.parentElement.insertBefore(row.firstChild, row);
        row.remove();
      }
      return;
    }

    if (!row) {
      row = document.createElement('div');
      row.className = 'sdf-glass-row';
    }
    // Same reasoning as the hero path: take the announcement bar's own slot so
    // the merchant's header-group ordering survives.
    if (!row.parentElement && announcement.parentElement) {
      announcement.parentElement.insertBefore(row, announcement);
    }
    // Guarded — an appendChild of an already-correct node still counts as a DOM
    // move, which disconnects/reconnects the custom element and restarts the
    // announcement carousel on every resize.
    if (announcement.parentElement !== row) row.appendChild(announcement);
    if (partner.parentElement !== row) row.appendChild(partner);
  }

  function relocateGlassShapes() {
    var hero = document.querySelector('.sindhi-hero');
    // No hero on this template — bars stay in normal flow and keep the plain
    // glass fill from the snippet's CSS (the deliberate fallback), but they can
    // still be paired into one row.
    if (!hero) { combineInFlow(); return; }

    // Remember each bar's real slot before the first move, so the mobile
    // branch (and any desktop→mobile resize) can put it back.
    if (origin) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-glass-shape]'), function (el) {
        if (!origin.has(el) && !el.classList.contains('is-hero-anchored')) {
          origin.set(el, { parent: el.parentElement, next: el.nextSibling });
        }
      });
    }

    // Mobile floats over the hero too (Sanjay, 2026-08-02: "on mobile, header
    // is not behind the header elements unlike web. It is leading to
    // backgrounds appearing"). This was gated to desktop earlier because the
    // bars collided with the fixed mobile header — but that collision was a
    // margin-blind offsetHeight sum in sindhi-header.liquid's glass-pin
    // handler, now fixed and extended to measure this stack, so the header
    // correctly pins below whatever floats above it at every width.

    if (getComputedStyle(hero).position === 'static') {
      hero.style.position = 'relative';
    }

    var stack = hero.querySelector('.' + STACK_CLASS);
    if (!stack) {
      stack = document.createElement('div');
      stack.className = STACK_CLASS;
      hero.insertBefore(stack, hero.firstChild);
    }

    // Purge stale copies. The theme editor re-renders a section's HTML into
    // its ORIGINAL slot on every setting change, which leaves whatever this
    // script previously moved into the hero behind as an orphan — without
    // this, editing settings piles up duplicate bars (seen live: ~6 stacked
    // copies of a 4-slide bar). A duplicate id means the fresh copy exists
    // elsewhere, so the one parked in our stack is the stale one.
    // Recurses into .sdf-glass-row: that wrapper legitimately has no
    // data-glass-shape of its own, so a flat pass would classify it as stale
    // and delete it together with both bars inside it (real bug — on the
    // second run every floating bar vanished from the page).
    (function purge(container) {
      Array.prototype.slice.call(container.children).forEach(function (child) {
        if (child.classList.contains('sdf-glass-row')) { purge(child); return; }
        var stale = !child.hasAttribute('data-glass-shape');
        if (!stale && child.id && document.querySelectorAll('[id="' + child.id + '"]').length > 1) {
          stale = true;
        }
        // Hiding a section in the theme editor removes its wrapper, but the
        // copy this script moved into the hero is no longer inside that
        // wrapper — so it survived and kept rendering, looking like a
        // hardcoded fallback that could not be switched off (Sanjay,
        // 2026-08-02). If the original slot is gone from the document, the
        // relocated node is an orphan and must go with it.
        if (!stale && isOrphaned(child)) stale = true;
        if (stale) child.remove();
      });
    })(stack);

    Array.prototype.forEach.call(document.querySelectorAll('[data-glass-shape]'), function (el) {
      if (el === stack) return;
      // Must re-check here as well as in the purge above: without it an
      // orphaned bar is deleted and then immediately re-adopted on the same
      // pass, so hiding a section would appear to do nothing.
      if (isOrphaned(el)) { el.remove(); return; }

      // No shape at THIS width — return it to normal flow. Checked before the
      // "already placed" test on purpose: a bar that floated on desktop and
      // has no mobile shape is sitting in a container right now, and an early
      // "already placed, skip" would strand it there after a resize.
      if (!activeShape(el)) { restoreOne(el); return; }

      var parent = el.parentElement;
      if (parent === stack) return;
      if (parent && parent.classList.contains('sdf-glass-row')) return;
      if (parent && parent.classList.contains('sdf-glass-belownav')) return;

      stack.appendChild(el);
      el.classList.add('is-hero-anchored');
    });

    // Combined row: a bar (currently the Location Bar) can opt to sit beside
    // the announcement bar instead of on its own line, saving a header layer.
    // Both must already be in the stack — i.e. both have a Header shape — so
    // this is a no-op when only one of them floats.
    // Mobile: the delivery bar moves BELOW the nav (Sanjay, 2026-08-02), so it
    // gets its own positioned container rather than living in the main stack.
    // Keeping it out of .sdf-glass-stack matters — sindhi-header.liquid pins
    // the nav below that stack's height, so leaving the delivery bar in it
    // would push the nav down and the bar would chase it forever.
    var isMobile = window.innerWidth <= 749;
    var delivery = hero.querySelector('[id^="slb-"][data-glass-shape]') ||
                   document.querySelector('[id^="slb-"][data-glass-shape]');
    var below = hero.querySelector('.sdf-glass-belownav');

    // activeShape, not just the marker: the marker means "a shape exists at
    // SOME breakpoint", so keying off it floated the delivery bar on mobile
    // even when only the desktop shape was set.
    if (isMobile && delivery && activeShape(delivery)) {
      if (!below) {
        below = document.createElement('div');
        below.className = 'sdf-glass-belownav';
        hero.appendChild(below);
      }
      Array.prototype.slice.call(below.children).forEach(function (c) { if (isOrphaned(c)) c.remove(); });
      if (!isOrphaned(delivery) && delivery.parentElement !== below) below.appendChild(delivery);
      delivery.classList.add('is-hero-anchored');
      // Position from the nav's MEASURED bottom rather than a CSS sum of
      // stack-height + nav-height. The nav is fixed and carries its own
      // transform offset, so those parts don't add up to where it actually
      // renders — the computed sum landed the delivery bar at y=158 while the
      // nav still occupied 120-176. Measuring the real edge is exact.
      var navRow = document.querySelector('.sh__nav-row') || document.querySelector('#sindhi-header');

      // The offset above is only recomputed on specific triggers (load,
      // resize, shopify:section:*, scroll-return-to-top) — none of which
      // fire when the nav's OWN size settles late for reasons unrelated to
      // any of those events: a web font swap reflowing the logo/nav text,
      // or the theme editor re-rendering the header section on a settings
      // change without also touching the location bar. When that happened,
      // the delivery bar stayed wherever the last (possibly pre-layout)
      // measurement had left it — reported live 2026-08-03 as the bar
      // "dangling" between a correct position and one overlapping the nav,
      // not a single consistently-wrong value. Observing navRow directly
      // closes that gap: any change to the nav's real size re-derives the
      // offset from it, independent of what caused the change.
      if (navRow && !navResizeObserver && 'ResizeObserver' in window) {
        navResizeObserver = new ResizeObserver(function () {
          clearTimeout(navResizeTimer);
          navResizeTimer = setTimeout(relocateGlassShapes, 50);
        });
        navResizeObserver.observe(navRow);
      }

      // Only measure at rest. The nav is position:fixed and rides up as the
      // announcement bar scrolls away, so its viewport rect describes the
      // at-rest layout only while the page is at the top. The delivery bar, by
      // contrast, is absolutely positioned inside the hero and scrolls with it —
      // the two coincide at scrollY 0 and nowhere else. Recomputing mid-scroll
      // subtracted a scrolled heroTop from a viewport-fixed navBottom, mixing
      // coordinate spaces: measured 2026-08-03, a recompute at scrollY 400 wrote
      // top:464px (correct value 95px), parking the bar far down the hero. That
      // is what made it "sometimes appear a lot below the glass nav" — in the
      // theme editor every setting change re-renders a section, so the recompute
      // landed at whatever scroll offset the merchant happened to be at. Skip the
      // write while scrolled; the scroll handler below redoes it at the top.
      if (navRow && window.scrollY <= 1) {
        var navBottom = navRow.getBoundingClientRect().bottom;
        var heroTop = hero.getBoundingClientRect().top;
        below.style.top = Math.max(0, Math.round(navBottom - heroTop + 16)) + 'px';

        // Align the right edge to the nav's own right edge. The mobile header
        // renders as separate pills inside a full-width row, so the row's
        // right edge (380) is NOT where the nav visually ends (368) — take the
        // rightmost visible pill instead, and fall back to the row if the
        // markup ever changes.
        var navRight = 0;
        Array.prototype.forEach.call(navRow.querySelectorAll('*'), function (el) {
          var b = el.getBoundingClientRect();
          if (b.width > 20 && b.height > 10) {
            var cs = getComputedStyle(el);
            if (cs.backgroundColor !== 'rgba(0, 0, 0, 0)' || cs.backdropFilter !== 'none') {
              navRight = Math.max(navRight, b.right);
            }
          }
        });
        if (!navRight) navRight = navRow.getBoundingClientRect().right;
        below.style.left = '0px';
        below.style.right = Math.max(0, Math.round(window.innerWidth - navRight)) + 'px';
        below.style.marginInline = '0';
      }
    } else if (below) {
      // Back to desktop — hand the bar back so the row/stack logic can place it.
      while (below.firstChild) stack.appendChild(below.firstChild);
      below.remove();
      below = null;
    }

    var partner = stack.querySelector('[data-glass-combine="true"]');
    var announcement = stack.querySelector('[id^="announcement-bar-"]');
    var row = stack.querySelector('.sdf-glass-row');
    if (partner && announcement && partner !== announcement) {
      if (!row) {
        row = document.createElement('div');
        row.className = 'sdf-glass-row';
      }
      // Take the announcement bar's OWN slot rather than jumping to the top of
      // the stack. Bars are appended in DOM order, which is the merchant's
      // header-group order, and inserting the row at stack.firstChild threw
      // that away — a utility bar ordered above the announcement bar still
      // rendered below it (Sanjay, 2026-08-02: "utility bar is not respecting
      // the position defined in header section settings").
      if (announcement.parentElement === stack) stack.insertBefore(row, announcement);
      else if (!row.parentElement) stack.appendChild(row);
      // Announcement first, delivery bar beside it. Guarded because every
      // appendChild of an already-correctly-placed node still counts as a DOM
      // move, which disconnects and reconnects the custom element — and each
      // reconnect restarts the announcement carousel. Re-running this function
      // (section load, resize) must therefore be a genuine no-op when nothing
      // has actually changed.
      if (announcement.parentElement !== row) row.appendChild(announcement);
      if (partner.parentElement !== row || partner.previousElementSibling !== announcement) {
        row.appendChild(partner);
      }
    } else if (row) {
      // Setting was switched back off (or its partner is gone) — move the bars
      // back out to the stack rather than leaving them orphaned inside a row
      // that no longer applies, then drop the empty wrapper.
      while (row.firstChild) stack.appendChild(row.firstChild);
      row.remove();
    }

    publishOffset(stack);

    // Heights change with viewport (text rewraps, per-breakpoint row heights,
    // rotation between differently-sized messages); a stale offset would put
    // the nav back on top of the bars.
    if (!resizeObserver && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(function () { publishOffset(stack); });
      resizeObserver.observe(stack);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateGlassShapes);
  } else {
    relocateGlassShapes();
  }

  // Theme editor: re-run after Shopify swaps a section's markup, otherwise the
  // freshly-rendered bar stays in the header group (unstyled by the anchored
  // rules) while the old one lingers in the hero.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (evt) {
    document.addEventListener(evt, relocateGlassShapes);
  });

  // Crossing the desktop/mobile boundary has to relocate or restore, since the
  // two layouts are structurally different (floating stack vs normal flow).
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(relocateGlassShapes, 150);
  });

  // The delivery bar's offset is only measurable at rest (see the belownav block
  // above), so any recompute that fired mid-scroll skipped its geometry write and
  // left the previous value in place. Re-run once the page returns to the top so
  // a setting changed while scrolled still lands correctly.
  var wasScrolled = false;
  window.addEventListener('scroll', function () {
    if (window.scrollY > 1) { wasScrolled = true; return; }
    if (wasScrolled) { wasScrolled = false; relocateGlassShapes(); }
  }, { passive: true });
})();
